import type { PlaceDto } from "@boomap/contracts";
import { useCallback, useEffect, useRef, useState } from "react";

import { useI18n } from "../i18n/index.ts";
import { apiErrorMessage } from "./api-error-message.ts";
import { searchPlaces } from "./api-client.ts";

/** Frappe au clavier : on attend une pause avant d'interroger l'annuaire. */
const DEBOUNCE_IN_MS = 350;

/** En dessous, aucune chance d'obtenir un résultat utile. */
export const MIN_QUERY_LENGTH = 2;

interface SearchState {
  readonly results: readonly PlaceDto[];
  readonly loading: boolean;
  readonly error: string | null;
  /** `true` dès qu'une recherche a abouti, même sans résultat. */
  readonly searched: boolean;
}

const IDLE: SearchState = { results: [], loading: false, error: null, searched: false };

export interface UsePlaceSearchResult extends SearchState {
  readonly query: string;
  readonly setQuery: (query: string) => void;
  readonly reset: () => void;
  /**
   * Inscrit un lieu choisi dans le champ sans relancer de recherche. Sans cela
   * le nom du lieu retenu serait aussitôt réinterrogé, et la liste se
   * réouvrirait sur le résultat qu'on vient de sélectionner.
   */
  readonly settle: (label: string) => void;
}

/**
 * Recherche de lieux au fil de la frappe.
 *
 * Deux précautions qui se voient peu et se paient cher : la requête en vol est
 * annulée à chaque nouvelle frappe — sinon les réponses arrivent dans le
 * désordre et la liste affiche les résultats d'une recherche déjà obsolète —
 * et l'état d'une recherche trop courte est **dérivé au rendu** plutôt
 * qu'écrasé depuis l'effet, ce qui éviterait un rendu en cascade.
 */
export const usePlaceSearch = (): UsePlaceSearchResult => {
  const { locale, t } = useI18n();
  const [query, setQuery] = useState("");
  const [state, setState] = useState<SearchState>(IDLE);
  const requestRef = useRef<AbortController | null>(null);
  const settledRef = useRef<string | null>(null);

  const terms = query.trim();
  const searchable = terms.length >= MIN_QUERY_LENGTH;

  const reset = useCallback(() => {
    requestRef.current?.abort();
    setState(IDLE);
  }, []);

  const settle = useCallback((label: string) => {
    requestRef.current?.abort();
    settledRef.current = label.trim();
    setQuery(label);
    setState(IDLE);
  }, []);

  useEffect(() => {
    // La saisie a bougé depuis la sélection : la recherche reprend ses droits.
    if (settledRef.current !== null && settledRef.current !== terms) {
      settledRef.current = null;
    }

    if (!searchable || settledRef.current === terms) {
      return undefined;
    }

    const timer = setTimeout(() => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;

      setState((previous) => ({ ...previous, loading: true }));

      void (async () => {
        try {
          const places = await searchPlaces(terms, locale, controller.signal);

          setState({ results: places, loading: false, error: null, searched: true });
        } catch (cause) {
          if (controller.signal.aborted) {
            return;
          }

          setState({
            results: [],
            loading: false,
            searched: false,
            error: apiErrorMessage(cause, t, t.errors.place_search_unavailable),
          });
        }
      })();
    }, DEBOUNCE_IN_MS);

    return () => clearTimeout(timer);
  }, [terms, searchable, locale, t]);

  useEffect(() => () => requestRef.current?.abort(), []);

  return {
    query,
    setQuery,
    reset,
    settle,
    // Sous le seuil, on n'affiche rien : ni ancien résultat, ni ancienne erreur.
    results: searchable ? state.results : [],
    loading: searchable && state.loading,
    error: searchable ? state.error : null,
    searched: searchable && state.searched,
  };
};
